import { Facade, vinject, vsingleton, VueModule } from '@niknevis/vue-modules'
import { RemoteData } from '@/modules/shared/helpers/http/remote-data'
import { Inertia } from '@/modules/shared/helpers/http/facades/inertia'
import { BootLayoutResource } from '@/modules/common/layout/models/layout-resource.model'
import { LayoutService } from '@/modules/common/layout/services/layout.service'
import { UserBootDataResourceModelInAdmin } from '@/modules/user/models/user-resource.model'
import AuthRoute from '@/modules/user/routes/auth.route'

type HandlerOrData = (() => any) | any

const returnHandlerOrData = (handlerOrData: HandlerOrData) => {
  if (typeof handlerOrData === 'function') {
    return handlerOrData()
  } else {
    return handlerOrData
  }
}

const defaultUser: UserBootDataResourceModelInAdmin = {
  id: 0,
  bot_activation: null,
  username: '',
  display_name: '',
  avatar: '',
  created_at: '',
  roles: [],
  permissions: []
}

@vsingleton()
export class UserBootUseCase {
  constructor(
    @vinject('rootModule') public rootModule: VueModule,
    private readonly service: LayoutService,
    private readonly authRoutes: AuthRoute
  ) {
    this.userType = this.rootModule.exports.userType
  }

  public readonly userType: string

  private remoteData = RemoteData.notAsked<BootLayoutResource>()

  get isLoading() {
    return this.remoteData.isInProgress()
  }

  get roles(): string[] {
    return this.remoteData.fold(
      () => [],
      () => [],
      value => value.data.user.roles,
      () => []
    )
  }

  get permissions(): string[] {
    return this.remoteData.fold(
      () => [],
      () => [],
      value => value.data.user.permissions,
      () => []
    )
  }

  get user(): UserBootDataResourceModelInAdmin {
    try {
      return this.remoteData.fold(
        () => defaultUser,
        () => defaultUser,
        value => value.data.user,
        () => defaultUser
      )
    } //todo Remove After Panels Wrap in Data
    catch (err) {
      return this.remoteData.fold(
        () => defaultUser,
        () => defaultUser,
        value => value['user'],
        () => defaultUser
      )
    }

  }

  getFrontData() {
    if (this.userType) {
      this.remoteData.fromPromise(
        this.service.bootData().then((resp) => {
          return resp
        })
      )
    }
  }

  logOut() {
    Inertia.post(
      this.authRoutes.logout()
    )
  }

  //---
  can(perm: string) {
    return this.permissions
      .includes(perm) || this.hasRole('superadmin')
  }

  canAny(perms: string[]) {
    return perms.some(
      perm => this.can(perm) || this.hasRole('superadmin')
    )
  }

  hasRole(role: string | string[]) {
    if (Array.isArray(role))
      return role.some(el => this.roles.includes(el))
    else return this.roles.includes(role)
  }

  hasRoleAny(roles: string[]) {
    return roles.some(
      role => this.hasRole(role)
    ) || this.hasRole('superadmin')
  }

  ifCan(perm: string, handlerOrData: HandlerOrData) {
    if (!!!perm) {
      return returnHandlerOrData(handlerOrData)
    }
    if (this.can(perm)) {
      return returnHandlerOrData(handlerOrData)
    } else {
      return null
    }
  }

  ifCanAny(perms: string[], handlerOrData: HandlerOrData) {
    if (!!!perms) {
      return returnHandlerOrData(handlerOrData)
    }
    if (this.canAny(perms)) {
      return returnHandlerOrData(handlerOrData)
    } else {
      return null
    }
  }

  ifHasRole(roles: string | string[], handlerOrData: HandlerOrData) {
    if (!!!roles) {
      return returnHandlerOrData(handlerOrData)
    }
    if (this.hasRole(roles)) {
      return returnHandlerOrData(handlerOrData)
    } else {
      return null
    }
  }

  ifHasRoleAny(roles: string[], handlerOrData: HandlerOrData) {
    if (!!!roles) {
      return returnHandlerOrData(handlerOrData)
    }
    if (this.hasRoleAny(roles)) {
      return returnHandlerOrData(handlerOrData)
    } else {
      return null
    }
  }

  init() {
    this.getFrontData()
  }
}

export class UserBoot extends Facade<LayoutService>() {
  static getFacadeAccessor() {
    return UserBootUseCase
  }
}
